import PropTypes from 'prop-types'
import styled from 'styled-components'
import { useState } from 'react'

import {
  Box,
  Button,
  Icon,
  Heading,
  Skeleton,
  Text,
  TransparentButton,
} from '@redriverpress/system'

import { MoveItemModal } from 'Packages/folders'
import { renderInlineMarkdown } from 'Common/utils/markdown'

import AddToClassDialog from 'Common/components/dialogs/AddToClassDialog'
import { CONTENT_TYPES } from 'Common/constants/contentTypes'

import AccessDialog from '../AccessDialog'
import useCategory from '../../hooks/useCategory'
import useGenre from '../../hooks/useGenre'
import useGenres from '../../hooks/useGenres'
import useSelection from '../../hooks/useSelection'

const CategorySelectionButton = styled(TransparentButton).attrs(props => ({
  borderRadius: props.theme.radii.rounded,
  fontWeight: 600,
  px: 2,
  py: 1,
}))`
  &:not([disabled]):hover {
    color: ${props => props.theme.colors.blue700};
  }

  &:focus {
    box-shadow: 0 0 0 2px ${props => props.theme.colors.blue500};
  }
`

export default function CategoryHeader({ userHasAccess, userLoggedIn }) {
  const { activeGenre } = useGenres()
  const { activeCategory } = useGenre(activeGenre?.id)
  const { isLoading, category } = useCategory(activeCategory?.id)
  const { selection, addToSelection, removeFromSelection } = useSelection()

  const selectedIds = selection.map(fc => fc.id)
  const categoryIds = category ? category.flashcards.map(fc => fc.id) : [] // category may not be immediately available

  // Check if there are ANY of the category's flashcards in the current selection
  const selectionHasSomeCategoryFlashcards =
    selection && selectedIds.some(id => categoryIds.includes(id))

  // Check if ALL of the category's flashcards are in the selection
  const selectionHasAllCategoryFlashcards =
    selection && categoryIds.every(id => selectedIds.includes(id))

  const [accessDialogOpen, setAccessDialogOpen] = useState(false)
  const [addToClassModalOpen, setAddToClassModalOpen] = useState(false)
  const [moveItemModalOpen, setMoveItemModalOpen] = useState(false)

  return isLoading || !category ? (
    <Box mb={3}>
      {/* Title */}
      <Box mb={1}>
        <Skeleton height='2.5rem' width={1 / 2} />
      </Box>
      {/* Flashcard count */}
      <Box mb={3}>
        <Skeleton height='1.6rem' width={1 / 4} />
      </Box>
      {/* Description */}
      <Box>
        <Skeleton height='1rem' width={1} mb={2} />
        <Skeleton height='1rem' width={2 / 3} mb={2} />
      </Box>
    </Box>
  ) : (
    <>
      <Box mb={3}>
        <Heading as='h3' mb={1}>
          {category.title}
        </Heading>
        <Text color='gray900' mb={category.description ? 3 : 0}>
          {category.flashcardsCount.toLocaleString()}&nbsp;flashcard
          {category.flashcardsCount !== 1 && 's'}
        </Text>
        {category.description && (
          <Text
            mt={0}
            fontSize={0}
            dangerouslySetInnerHTML={{
              __html: renderInlineMarkdown(category.description),
            }}
          />
        )}
        <Box my={3} display={['block', null, 'flex']}>
          <Button
            small
            as={userHasAccess ? 'a' : 'button'}
            href={
              userHasAccess
                ? `/flashcard_player?flashcard_category=${category.id}`
                : null
            }
            onClick={userHasAccess ? null : () => setAccessDialogOpen(true)}
          >
            <Icon icon='rocket' pr={2} />
            Launch Slideshow
          </Button>
          <Button
            small
            mt={[2, null, 0]}
            ml={[0, 2]}
            onClick={() =>
              userHasAccess
                ? setAddToClassModalOpen(true)
                : setAccessDialogOpen(true)
            }
          >
            <Icon icon='plus' pr={2} />
            Add to class
          </Button>

          {/* Save to folder Button */}
          {userLoggedIn && (
            <TransparentButton
              ml={2}
              bg='white'
              size='2.25rem'
              borderRadius='small'
              title='Save flashcard category to folder'
              onClick={() => setMoveItemModalOpen(true)}
              boxShadow={3}
            >
              <Icon icon='folder-open' />
            </TransparentButton>
          )}
        </Box>
        <Box>
          <CategorySelectionButton
            ml={-2}
            mr={3}
            disabled={!selectionHasSomeCategoryFlashcards}
            onClick={() =>
              userHasAccess
                ? removeFromSelection(categoryIds)
                : setAccessDialogOpen(true)
            }
          >
            <Icon icon='times' pr={2} />
            Deselect All
          </CategorySelectionButton>
          <CategorySelectionButton
            disabled={selectionHasAllCategoryFlashcards}
            onClick={() =>
              userHasAccess
                ? addToSelection(category.flashcards)
                : setAccessDialogOpen(true)
            }
          >
            <Icon icon='check' pr={2} />
            Select All
          </CategorySelectionButton>
        </Box>
      </Box>
      {!userHasAccess && (
        <AccessDialog
          open={accessDialogOpen}
          onOpenChange={open => setAccessDialogOpen(open)}
        />
      )}
      <AddToClassDialog
        open={addToClassModalOpen}
        onOpenChange={() => setAddToClassModalOpen(false)}
        onCancel={() => setAddToClassModalOpen(false)}
        contentType={CONTENT_TYPES.flashcardCategory}
        getLegacyURL={selectedClass =>
          `/flashcard_sets/category/share/${category.id}/${selectedClass.id}`
        }
        content={category}
      />
      {!isLoading && !!category && (
        <MoveItemModal
          isOpen={moveItemModalOpen}
          item={{
            fileable: {
              id: category?.id,
              type: CONTENT_TYPES.flashcardCategory,
            },
          }}
          onClose={reload => {
            setMoveItemModalOpen(false)
            reload && window.location.reload()
          }}
        />
      )}
    </>
  )
}

CategoryHeader.defaultProps = {
  userLoggedIn: false,
}

CategoryHeader.propTypes = {
  userHasAccess: PropTypes.bool.isRequired,
  userLoggedIn: PropTypes.bool,
}
